import { Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from '../core/auth/auth.service';
import { SessionStore } from '../core/auth/session.store';
import { AccountProfileComponent } from '../components/account-profile/account-profile.component';
import { TranslationService } from '../core/i18n/translation.service';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { SubmenuComponent } from '../components/submenu/submenu.component';
import {
  MenuItemId,
  SidebarActionId,
  SidebarMenuComponent,
} from '../components/sidebar-menu/sidebar-menu.component';
import { ChecklistCreatorComponent } from './checklists/components/checklist-creator.component';
import { ChecklistListComponent } from './checklists/components/checklist-list.component';
import { UserRole } from '../core/auth/auth-session.model';
import { AuditExecutionComponent } from './audits/components/audit-execution.component';
import { AuditCreatorComponent } from './audits/components/audit-creator.component';
import { AuditListComponent } from './audits/components/audit-list.component';
import { DashboardComponent } from './dashboard/components/dashboard.component';
import { ReportsComponent } from './reports/components/reports.component';
import { AuditsApi } from './audits/services/audits.api';
import { UserAdminComponent } from './users/components/user-admin.component';
import { IssueReportModalComponent } from './issues/components/issue-report-modal.component';
import { IssueListComponent } from './issues/components/issue-list.component';
import { IssueEditorComponent } from './issues/components/issue-editor.component';
import { IssueDto } from './issues/models/issue.models';
import { AuditAssistantComponent } from './audits/components/audit-assistant.component';
import { AuditUnlockNotificationsComponent } from './audits/components/audit-unlock-notifications.component';

const MENU_ITEM_IDS: MenuItemId[] = [
  'dashboard',
  'audits',
  'checklists',
  'users',
  'tenants',
  'corrective-actions',
  'reports',
];

function isMenuItemId(value: string | null): value is MenuItemId {
  return value !== null && MENU_ITEM_IDS.includes(value as MenuItemId);
}

@Component({
  selector: 'app-main-page',
  imports: [
    AccountProfileComponent,
    SidebarMenuComponent,
    SubmenuComponent,
    ConfirmationDialogComponent,
    ChecklistCreatorComponent,
    ChecklistListComponent,
    AuditCreatorComponent,
    AuditListComponent,
    AuditExecutionComponent,
    DashboardComponent,
    ReportsComponent,
    UserAdminComponent,
    IssueReportModalComponent,
    IssueListComponent,
    IssueEditorComponent,
    AuditAssistantComponent,
    AuditUnlockNotificationsComponent,
  ],
  templateUrl: './main-page.component.html',
  styleUrl: './main-page.component.css',
})
export class MainPageComponent implements OnInit {
  protected readonly auditAssistant = viewChild(AuditAssistantComponent);
  private readonly authService = inject(AuthService);
  private readonly auditsApi = inject(AuditsApi);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private pendingChecklistCreatorDiscardAction: (() => void) | null = null;

  protected readonly i18n = inject(TranslationService);
  protected readonly sessionStore = inject(SessionStore);
  protected readonly isMenuOpen = signal(false);
  protected readonly activeItemId = signal<MenuItemId>('dashboard');
  protected readonly activeExecutionPlanId = signal<string | null>(null);
  protected readonly isAuditCreatorOpen = signal(false);
  protected readonly editingAuditId = signal<string | null>(null);
  protected readonly auditSearchQuery = signal('');
  protected readonly isChecklistCreatorOpen = signal(false);
  protected readonly editingChecklistId = signal<string | null>(null);
  protected readonly checklistCreatorHasUnsavedChanges = signal(false);
  protected readonly discardDialogOpen = signal(false);
  protected readonly discardDialogMessageKey = signal('checklistCreator.confirmDiscardChanges');
  protected readonly checklistSearchQuery = signal('');
  protected readonly isUserCreatorOpen = signal(false);
  protected readonly userSearchQuery = signal('');
  protected readonly userListViewRequest = signal(0);
  protected readonly isIssueReportOpen = signal(false);
  protected readonly issueReportSuccessVisible = signal(false);
  protected readonly issueSearchQuery = signal('');
  protected readonly issueListReloadRequest = signal(0);
  protected readonly editingIssue = signal<IssueDto | null>(null);
  protected readonly currentUserRole = computed<UserRole>(
    () => this.sessionStore.session()?.role ?? 'BUSINESS_ADMIN',
  );
  protected readonly userLocationNames = signal<string[]>([]);
  private issueReportSuccessTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.issueReportSuccessTimeout) {
        clearTimeout(this.issueReportSuccessTimeout);
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.activeExecutionPlanId.set(params.get('planId'));
    });

    const requestedSection = this.route.snapshot.queryParamMap.get('section');
    if (isMenuItemId(requestedSection)) {
      this.activeItemId.set(requestedSection);
    }
    const requestedChecklist = this.route.snapshot.queryParamMap.get('editChecklist');
    if (!this.activeExecutionPlanId() && requestedSection === 'checklists' && requestedChecklist &&
        /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(requestedChecklist)) {
      this.openChecklistEditor(requestedChecklist);
    }

    const session = this.sessionStore.snapshot();
    const userCode = session?.userCode;
    if (!userCode) {
      return;
    }

    this.auditsApi.getUserLocations(userCode).subscribe({
      next: ({ locations }) => {
        this.userLocationNames.set(locations.map(({ name }) => name));
      },
      error: () => {
        this.userLocationNames.set([]);
      },
    });
  }

  openMenu(): void {
    this.isMenuOpen.set(true);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  handleMenuItemSelection(itemId: MenuItemId): void {
    if (this.activeExecutionPlanId()) {
      this.closeMenu();
      void this.router.navigate(['/app'], { queryParams: { section: itemId } });
      return;
    }

    this.activeItemId.set(itemId);
    this.isChecklistCreatorOpen.set(false);
    if (itemId !== 'checklists') {
      this.checklistSearchQuery.set('');
    }
    if (itemId !== 'audits') {
      this.auditSearchQuery.set('');
      this.isAuditCreatorOpen.set(false);
      this.editingAuditId.set(null);
    }
    if (itemId !== 'users') {
      this.userSearchQuery.set('');
      this.isUserCreatorOpen.set(false);
    }
    if (itemId !== 'corrective-actions') {
      this.issueSearchQuery.set('');
      this.editingIssue.set(null);
    }
    this.closeMenu();
  }

  handleSidebarAction(actionId: SidebarActionId): void {
    if (actionId === 'report-incident') {
      this.openIssueReport();
    }
    this.closeMenu();
  }

  openIssueReport(): void {
    this.isIssueReportOpen.set(true);
  }

  closeIssueReport(): void {
    this.isIssueReportOpen.set(false);
  }

  handleIssueReported(): void {
    this.isIssueReportOpen.set(false);
    this.issueListReloadRequest.update((request) => request + 1);
    this.issueReportSuccessVisible.set(true);

    if (this.issueReportSuccessTimeout) {
      clearTimeout(this.issueReportSuccessTimeout);
    }
    this.issueReportSuccessTimeout = setTimeout(() => {
      this.issueReportSuccessVisible.set(false);
      this.issueReportSuccessTimeout = null;
    }, 5000);
  }

  dismissIssueReportSuccess(): void {
    this.issueReportSuccessVisible.set(false);
    if (this.issueReportSuccessTimeout) {
      clearTimeout(this.issueReportSuccessTimeout);
      this.issueReportSuccessTimeout = null;
    }
  }

  handleIssueSearch(query: string): void {
    this.editingIssue.set(null);
    this.issueSearchQuery.set(query);
  }

  openIssueEditor(issue: IssueDto): void {
    this.editingIssue.set(issue);
  }

  openDashboardIssue(issue: IssueDto): void {
    this.activeItemId.set('corrective-actions');
    this.openIssueEditor(issue);
  }

  openNotificationIssue(issue: IssueDto): void {
    this.activeItemId.set('corrective-actions');
    this.openIssueEditor(issue);
    if (this.activeExecutionPlanId()) {
      void this.router.navigate(['/app'], { queryParams: { section: 'corrective-actions' } });
      return;
    }
  }

  createDashboardAudit(): void {
    this.activeItemId.set('audits');
    this.openAuditCreator();
  }

  closeIssueEditor(): void {
    this.editingIssue.set(null);
  }

  handleIssueSaved(): void {
    this.editingIssue.set(null);
    this.issueListReloadRequest.update((request) => request + 1);
  }

  handleIssueDecisionUpdated(): void {
    this.issueListReloadRequest.update((request) => request + 1);
  }

  closeAuditExecution(): void {
    void this.router.navigateByUrl('/app');
  }

  openAuditExecutionPreview(planId: string): void {
    void this.router.navigate(['/app/audit-executions', planId]);
  }

  openAuditCreator(): void {
    this.editingAuditId.set(null);
    this.isAuditCreatorOpen.set(true);
  }

  closeAuditCreator(): void {
    this.editingAuditId.set(null);
    this.isAuditCreatorOpen.set(false);
  }

  openAuditEditor(auditId: string): void {
    this.editingAuditId.set(auditId);
    this.isAuditCreatorOpen.set(true);
  }

  handleAuditSearch(query: string): void {
    this.closeAuditCreator();
    this.auditSearchQuery.set(query);
  }

  openUserCreator(): void {
    this.isUserCreatorOpen.set(true);
  }

  closeUserCreator(): void {
    this.isUserCreatorOpen.set(false);
  }

  handleUserSearch(query: string): void {
    this.closeUserCreator();
    this.userSearchQuery.set(query);
    this.userListViewRequest.update((request) => request + 1);
  }

  openChecklistCreator(): void {
    if (
      !this.requestChecklistCreatorDiscard('checklistCreator.confirmDiscardChanges', () => {
        this.openNewChecklistCreator();
      })
    ) {
      return;
    }

    this.openNewChecklistCreator();
  }

  private openNewChecklistCreator(): void {
    this.editingChecklistId.set(null);
    this.checklistCreatorHasUnsavedChanges.set(false);
    this.isChecklistCreatorOpen.set(true);
  }

  closeChecklistCreator(): void {
    this.editingChecklistId.set(null);
    this.checklistCreatorHasUnsavedChanges.set(false);
    this.isChecklistCreatorOpen.set(false);
  }

  openChecklistEditor(checklistId: string): void {
    this.editingChecklistId.set(checklistId);
    this.checklistCreatorHasUnsavedChanges.set(false);
    this.isChecklistCreatorOpen.set(true);
  }

  openAssistantChecklist(checklistId: string): void {
    if (!/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(checklistId)) return;
    const open = () => {
      if (this.activeExecutionPlanId()) {
        void this.router.navigate(['/app'], { queryParams: { section: 'checklists', editChecklist: checklistId } });
        return;
      }
      this.handleMenuItemSelection('checklists');
      this.openChecklistEditor(checklistId);
    };
    if (this.requestChecklistCreatorDiscard('checklistCreator.confirmDiscardChanges', open)) open();
  }

  handleChecklistCreatorUnsavedChanges(hasUnsavedChanges: boolean): void {
    this.checklistCreatorHasUnsavedChanges.set(hasUnsavedChanges);
  }

  handleChecklistSearch(query: string): void {
    if (
      !this.requestChecklistCreatorDiscard('checklistCreator.confirmDiscardForSearch', () => {
        this.closeChecklistCreator();
        this.checklistSearchQuery.set(query);
      })
    ) {
      return;
    }

    this.closeChecklistCreator();
    this.checklistSearchQuery.set(query);
  }

  confirmChecklistCreatorDiscard(): void {
    const action = this.pendingChecklistCreatorDiscardAction;
    this.clearChecklistCreatorDiscardDialog();
    action?.();
  }

  cancelChecklistCreatorDiscard(): void {
    this.clearChecklistCreatorDiscardDialog();
  }

  private requestChecklistCreatorDiscard(messageKey: string, action: () => void): boolean {
    if (!this.isChecklistCreatorOpen() || !this.checklistCreatorHasUnsavedChanges()) {
      return true;
    }

    this.pendingChecklistCreatorDiscardAction = action;
    this.discardDialogMessageKey.set(messageKey);
    this.discardDialogOpen.set(true);
    return false;
  }

  private clearChecklistCreatorDiscardDialog(): void {
    this.pendingChecklistCreatorDiscardAction = null;
    this.discardDialogOpen.set(false);
  }

  logout(): void {
    this.authService.logout();
    void this.router.navigateByUrl('/login');
  }
}
